import type * as THREE from "three";

/**
 * A unit killed by a round still in the air walks on until the round lands (statusFx.ts ImpactGate), the "ghost walk".
 *
 * The sim resolves a tower's damage the tick it fires (combat.ts fireTowers) while the round is drawn leaving the muzzle
 * on the clip's release frame and flying after that: a cannonball ~0.4 s, a fireball up to ~1 s. The views held a
 * doomed body where the sim killed it, so it stood frozen for that long and then died as the ball landed (owner
 * report 2026-09-30: "they stop before the cannon ball lands, freezing and then dying"). Now the body keeps its last
 * pace along the road, one sim tick at a time as the living do, so it is still walking when the round lands on it.
 * Presentation only: the sim and its goldens are untouched.
 */

/** A ghost stops this far short of the road's end, so a doomed body never walks into the fortress it cannot reach. */
export const GHOST_END_MARGIN = 0.35;

/** The road as the view reads it: cell centres from the portal to the base (LevelSim.road.roadCells). */
export type RoadCells = readonly { readonly x: number; readonly y: number }[];

/** `positionAt` (domain roadGeometry.ts) in plain numbers, for the view: the point `distance` cells along the road, into `out` (x, 0, z). */
export function roadPoint(road: RoadCells, distance: number, out: THREE.Vector3): THREE.Vector3 {
  const last = road.length - 1;
  if (last < 0) return out.set(0, 0, 0);
  if (distance <= 0) return out.set(road[0].x, 0, road[0].y);
  const i = Math.floor(distance);
  if (i >= last) return out.set(road[last].x, 0, road[last].y);
  const f = distance - i, a = road[i], b = road[i + 1];
  return out.set(a.x + (b.x - a.x) * f, 0, a.y + (b.y - a.y) * f);
}

/** How far along the road a doomed body walks on each tick, and where it is: set when it is killed, stepped on each tick. */
export interface Ghost { dist: number; step: number }

/**
 * The ghost of a body killed at road `distance`, whose last tick carried it from `prev` to `cur` (world x, z): it keeps
 * that pace, which already carries its slow, the mud and a troop's block (a blocked body stands still).
 */
export function ghostOf(distance: number, prev: THREE.Vector3, cur: THREE.Vector3, into: Ghost): Ghost {
  into.dist = distance;
  into.step = Math.hypot(cur.x - prev.x, cur.z - prev.z);
  return into;
}

/** One sim tick of a ghost's walk: it moves on by its pace (never past the road's end margin) and `cur` follows. */
export function ghostTick(g: Ghost, road: RoadCells, cur: THREE.Vector3): void {
  const end = Math.max(g.dist, road.length - 1 - GHOST_END_MARGIN);
  g.dist = Math.min(end, g.dist + g.step);
  roadPoint(road, g.dist, cur);
}
